import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { DateState } from '@Components/DateRangePicker';
import DNCLayout from '@Components/DncLayout';
import { MenuItem } from '@Components/PopOverMenu';
import Table, { TableHead } from '@Components/Table';
import { CommonConstants } from '@Constants/index';
import { HeaderCheckboxState, Icon } from '@Typings/global.types';
import {
  getCheckableCount,
  getDateFromISODateTimeString,
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import DncTableProps from './DncTable.types';

const DNC: FunctionComponent<DncTableProps> = (
  props: DncTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const { tableProps, handleBulk, ...resProps } = props;
  const {
    loading,
    rows,
    paginationProps,
    setChecked,
    checked,
    ...resTableProps
  } = tableProps;
  const { count, ...resPaginationProps } = paginationProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [showFilterBtn, setShowFilterBtn] = useState<boolean>(false);
  const [headerCheckboxState, setHeaderCheckboxState] =
    useState<HeaderCheckboxState>({ checked: false, indeterminate: false });

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const phoneParam =
      (query.get(CommonConstants.QUERY_PARAMS.DNC.PHONE) as string) || '';
    const dateStartParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_START) as string) ||
        ''
    );
    const dateEndParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_END) as string) || ''
    );
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    /** SET STATES */
    setPage(pageNo);
    setRowsPerPage(pageSize);
    setSearchValue(phoneParam);
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: dateStartParam,
        endDate: dateEndParam,
      },
    ]);
    setShowFilterBtn(!!(phoneParam || dateStartParam || dateEndParam));
  }, [setPage, setRowsPerPage]);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** EDGE CASE
   * Append Required URL Params
   */
  useEffect(() => {
    const requiredParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
        `${CommonConstants.INITIAL_ROWS_PER_PAGE}`,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
        `${CommonConstants.INITIAL_PAGE_NO}`,
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
        `${CommonConstants.INITIAL_DNC_SORT_ORDER}`,
      [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
        `${CommonConstants.INITIAL_SORT_DIRECTION}`,
    });

    changeRoute(requiredParams);
  }, [changeRoute]);

  /** Count of non disabled rows */
  const checkableCount = getCheckableCount(rows);

  /** PAGINATION HANDLERS --> */
  /** ROWS PER PAGE OPTIONS */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );

    changeRoute(pageParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });

    changeRoute(pageParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** TABLE HEADER CHECKBOX HANDLERS --> */
  /** Clears all checkboxes. */
  const clearSelection = (): void => {
    if (setChecked) {
      setChecked([]);
    }
    // Allows header checkbox to update when all rows are disabled
    setHeaderCheckboxState({ checked: false, indeterminate: false });
    handleBulk(0);
  };

  /** Selects the checkboxes of current page. */
  const selectPage = (): void => {
    clearSelection();
    setHeaderCheckboxState({ checked: true, indeterminate: false });
    if (setChecked) {
      setChecked(rows.filter((row) => !row.disabled).map((row) => row.id));
    }
  };

  /** Selects the first 1000 rows
   * Select all rows if less than 1000
   */
  const select1000 = () => {
    clearSelection();
    handleBulk(Math.min(count, CommonConstants.BULK_COUNT));
  };

  /** Table Head Checkbox Handler */
  const headerCheckClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked && !headerCheckboxState.indeterminate) {
      selectPage();
    } else {
      clearSelection();
    }
  };

  /** HANDLES THE TABLE HEADER CHECKBOX STATE (checked and indeterminate) */
  useEffect(() => {
    if (!loading) {
      if (checked?.length && checked.length !== checkableCount) {
        setHeaderCheckboxState({ checked: false, indeterminate: true });
      } else if (checked?.length && checked.length === checkableCount) {
        setHeaderCheckboxState({ checked: true, indeterminate: false });
      } else {
        setHeaderCheckboxState({ checked: false, indeterminate: false });
      }
    }
  }, [checked?.length, loading, checkableCount]);

  /** TABLE HEAD CHECKBOX MENU OPTIONS */
  const menuOptions: MenuItem[] = [
    {
      label: t('common.table.Select Page'),
      value: 'page',
      onClick: selectPage,
    },
    {
      label: t('common.table.Select first 1000'),
      value: '1000',
      onClick: select1000,
    },
    {
      label: t('common.table.Clear Selection'),
      value: 'clear',
      onClick: clearSelection,
    },
  ];
  /** <-- TABLE HEADER CHECKBOX HANDLERS */

  /** FILTERS --> */
  /** ON SEARCH HANDLER */
  const onSearch = (value: string) => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.DNC.PHONE,
      value
    );
    changeRoute(searchParam);

    // Clear bulk selection (if any) when user applies search filter
    clearSelection();
  };

  /** ON DATE RANGE FILTER SELECT */
  const getDateState = (startDate?: Date, endDate?: Date) => {
    const dateParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
    });

    changeRoute(dateParams);

    // Clear bulk selection (if any) when user applies date filter
    clearSelection();
  };

  /** CLEAR FILTER BUTTON CLICK HANDLER */
  const handleClearFilterBtnClick = () => {
    const params = query.setAll({
      [CommonConstants.QUERY_PARAMS.DNC.PHONE]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]: '',
    });

    changeRoute(params);
  };
  /** <-- FILTERS */

  /** TABLE --> */
  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER);
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          order || CommonConstants.INITIAL_DNC_SORT_ORDER,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('common.table.DNC Number'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.PHONE),
        width: 22.2,
        onClick: () => handleTableHeadClick(CommonConstants.DNC_SORT.PHONE),
      },
      {
        label: t('common.table.Created Date'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.CREATE_DATE),
        width: 17.2,
        align: 'center',
        onClick: () =>
          handleTableHeadClick(CommonConstants.DNC_SORT.CREATE_DATE),
      },
      {
        label: t('common.table.Last Updated'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.LAST_UPDATE_DATE),
        width: 17.2,
        align: 'center',
        onClick: () =>
          handleTableHeadClick(CommonConstants.DNC_SORT.LAST_UPDATE_DATE),
      },
      {
        label: t('common.table.Reasons'),
        icon: getTableHeadIcon(CommonConstants.DNC_SORT.REASON),
        width: 17.2,
        onClick: () => handleTableHeadClick(CommonConstants.DNC_SORT.REASON),
      },
      {
        label: t('common.table.Comments'),
        width: 31,
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];
  };

  /** DNC TABLE COMPONENT */
  const TableComponent = (
    <Table
      loading={loading}
      rows={rows}
      tableHead={getTableHead()}
      checked={checked}
      setChecked={setChecked}
      checkboxMenuProps={{
        menuOptions,
        checkboxProps: {
          checked: headerCheckboxState.checked,
          indeterminate: headerCheckboxState.indeterminate,
          onChange: headerCheckClickHandler,
          disabled: !rows.length,
        },
      }}
      paginationProps={{
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...resPaginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  return (
    <DNCLayout
      showFilterControls={showFilterBtn}
      clearFilterBtnProps={{
        onClick: handleClearFilterBtnClick,
        disabled: loading,
      }}
      cancelBtnProps={{ onClick: clearSelection, disabled: loading }}
      selectedBtnProps={{ onClick: clearSelection, disabled: loading }}
      searchProps={{
        value: searchValue,
        setValue: setSearchValue,
        placeholder: t('common.tableFilters.Search by number'),
        onSearch,
      }}
      dateRangeProps={{
        dateState,
        setDateState,
        getDateState,
      }}
      table={TableComponent}
      {...resProps}
    />
  );
};

export default DNC;
